import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { MessageSquare, Mail, Save, RefreshCw, Eye, EyeOff, Loader2, Copy } from 'lucide-react';

// Variabili disponibili con descrizione
const VARIABLES = [
  { key: '{nome}',      label: 'Nome ospite',     example: 'Marco Rossi' },
  { key: '{struttura}', label: 'Nome struttura',   example: 'ShangriLux Hotels' },
  { key: '{checkin}',   label: 'Data check-in',    example: '1 aprile 2026' },
  { key: '{checkout}',  label: 'Data check-out',   example: '5 aprile 2026' },
  { key: '{link}',      label: 'Link chiave',       example: 'https://shangrilux.com/g/abc123' },
];

const DEMO = {
  nome:      'Marco Rossi',
  struttura: 'ShangriLux Hotels',
  checkin:   '1 aprile 2026',
  checkout:  '5 aprile 2026',
  link:      `${window.location.origin}/g/chiave-demo-ospite`,
};

function fillTemplate(tpl) {
  if (!tpl) return '';
  return tpl
    .replace(/{nome}/g,      DEMO.nome)
    .replace(/{struttura}/g, DEMO.struttura)
    .replace(/{checkin}/g,   DEMO.checkin)
    .replace(/{checkout}/g,  DEMO.checkout)
    .replace(/{link}/g,      DEMO.link);
}

// Inserisce la variabile alla posizione corrente del cursore nel textarea
function insertAtCursor(ref, variable, value, setter) {
  const el = ref.current;
  if (!el) { setter(v => v + variable); return; }
  const start = el.selectionStart ?? value.length;
  const end   = el.selectionEnd   ?? value.length;
  const next  = value.slice(0, start) + variable + value.slice(end);
  setter(next);
  // Ripristina il cursore dopo il render
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + variable.length, start + variable.length);
  });
}

// Pill cliccabile per ogni variabile
function VarPill({ variable, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`var-pill-${variable.key.replace(/[{}]/g, '')}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono transition-all hover:scale-105 active:scale-95"
      style={{
        background: 'rgba(201,168,76,0.12)',
        border: '1px solid rgba(201,168,76,0.35)',
        color: '#c9a84c',
      }}
      title={`Inserisci ${variable.label} (esempio: ${variable.example})`}
    >
      {variable.key}
    </button>
  );
}

export function MessageTemplates() {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [preview, setPreview]   = useState(false);

  const [whatsapp, setWhatsapp]       = useState('');
  const [emailSubj, setEmailSubj]     = useState('');
  const [emailBody, setEmailBody]     = useState('');

  const waRef   = useRef(null);
  const subjRef = useRef(null);
  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/settings');
      setWhatsapp(data.msg_whatsapp   || '');
      setEmailSubj(data.msg_email_subject || '');
      setEmailBody(data.msg_email_body    || '');
    } catch { toast.error('Errore caricamento modelli'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        msg_whatsapp:       whatsapp,
        msg_email_subject:  emailSubj,
        msg_email_body:     emailBody,
      });
      toast.success('Modelli salvati');
    } catch { toast.error('Errore salvataggio'); }
    finally   { setSaving(false); }
  };

  const copyPreview = async (text) => {
    try {
      await navigator.clipboard.writeText(fillTemplate(text));
      toast.success('Testo copiato');
    } catch { toast.error('Errore copia'); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-[#c9a84c]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="label-gold">Comunicazione</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Modelli Messaggi</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--lux-text-muted)' }}>
            Personalizza i testi inviati agli ospiti via WhatsApp ed Email. Usa le variabili per inserire dati dinamici.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={load} className="btn-ghost p-2" title="Ricarica" data-testid="reload-templates-btn">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => setPreview(p => !p)}
            data-testid="toggle-preview-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-sm transition-all"
            style={{
              background: preview ? 'rgba(201,168,76,0.15)' : 'var(--lux-surface-2)',
              border: `1px solid ${preview ? 'rgba(201,168,76,0.4)' : 'var(--lux-border-2)'}`,
              color: preview ? '#c9a84c' : 'var(--lux-text-muted)',
            }}
          >
            {preview ? <EyeOff size={14} /> : <Eye size={14} />}
            {preview ? 'Editor' : 'Anteprima'}
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-templates-btn"
            className="btn-gold flex items-center gap-2"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Salva
          </button>
        </div>
      </div>

      {/* Legenda variabili */}
      <div className="card-dark rounded-lg p-4">
        <p className="text-xs uppercase tracking-widest mb-3 text-left" style={{ color: 'var(--lux-text-muted-3)' }}>
          Variabili disponibili — usa le pill nelle sezioni sottostanti per inserirle
        </p>
        <div className="flex flex-wrap gap-2">
          {VARIABLES.map(v => (
            <div key={v.key} className="flex flex-col items-start gap-0.5">
              <span className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--lux-text-muted-3)' }}>
                {v.label}
              </span>
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono cursor-default"
                style={{
                  background: 'rgba(201,168,76,0.08)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  color: '#c9a84c',
                }}
              >
                {v.key}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHATSAPP ── */}
      <section className="card-dark rounded-lg overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--lux-border-1)', background: 'rgba(37,211,102,0.05)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#25D366' }}>
            <MessageSquare size={15} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <p className="text-white text-sm font-medium">Messaggio WhatsApp</p>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>Testo inviato quando clicchi "WhatsApp" nel modal QR</p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          {!preview ? (
            <>
              <div className="flex flex-wrap gap-1.5 mb-1">
                {VARIABLES.map(v => (
                  <VarPill key={v.key} variable={v} onClick={() => insertAtCursor(waRef, v.key, whatsapp, setWhatsapp)} />
                ))}
              </div>
              <textarea
                ref={waRef}
                value={whatsapp}
                onChange={e => setWhatsapp(e.target.value)}
                data-testid="whatsapp-template-input"
                rows={6}
                placeholder="Scrivi il messaggio WhatsApp..."
                className="input-dark w-full font-mono text-sm resize-y"
                style={{ minHeight: '140px' }}
              />
              <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>
                {whatsapp.length} caratteri
              </p>
            </>
          ) : (
            <PreviewBox text={whatsapp} type="whatsapp" onCopy={() => copyPreview(whatsapp)} />
          )}
        </div>
      </section>

      {/* ── EMAIL ── */}
      <section className="card-dark rounded-lg overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--lux-border-1)', background: 'rgba(201,168,76,0.04)' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.2)', border: '1px solid rgba(201,168,76,0.4)' }}>
            <Mail size={15} color="#c9a84c" strokeWidth={2} />
          </div>
          <div>
            <p className="text-white text-sm font-medium">Messaggio Email</p>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted-3)' }}>Subject e corpo dell'email inviata quando clicchi "Email" nel modal QR</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {/* Subject */}
          <div>
            <label className="label-muted block mb-1.5">Oggetto (Subject)</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {VARIABLES.filter(v => v.key !== '{link}').map(v => (
                <VarPill key={v.key} variable={v} onClick={() => insertAtCursor(subjRef, v.key, emailSubj, setEmailSubj)} />
              ))}
            </div>
            <input
              ref={subjRef}
              value={emailSubj}
              onChange={e => setEmailSubj(e.target.value)}
              data-testid="email-subject-input"
              className="input-dark w-full"
              placeholder="Es: La tua chiave digitale — {struttura}"
            />
          </div>

          {/* Body */}
          <div>
            <label className="label-muted block mb-1.5">Corpo del messaggio</label>
            {!preview ? (
              <>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {VARIABLES.map(v => (
                    <VarPill key={v.key} variable={v} onClick={() => insertAtCursor(bodyRef, v.key, emailBody, setEmailBody)} />
                  ))}
                </div>
                <textarea
                  ref={bodyRef}
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  data-testid="email-body-input"
                  rows={8}
                  placeholder="Scrivi il corpo dell'email..."
                  className="input-dark w-full font-mono text-sm resize-y"
                  style={{ minHeight: '180px' }}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--lux-text-muted-3)' }}>
                  {emailBody.length} caratteri
                </p>
              </>
            ) : (
              <div className="space-y-3">
                <div className="rounded-sm px-4 py-2.5 text-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)', color: 'var(--lux-text-muted)' }}>
                  <span className="text-xs uppercase tracking-widest mr-2" style={{ color: 'var(--lux-text-muted-3)' }}>Oggetto:</span>
                  {fillTemplate(emailSubj)}
                </div>
                <PreviewBox text={emailBody} type="email" onCopy={() => copyPreview(emailBody)} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Nota */}
      <p className="text-xs pb-4" style={{ color: 'var(--lux-text-muted-3)' }}>
        I modelli vengono applicati automaticamente ai pulsanti WhatsApp ed Email nel modal QR delle Chiavi Virtuali.
      </p>
    </div>
  );
}

// Componente anteprima
function PreviewBox({ text, type, onCopy }) {
  const filled = fillTemplate(text);
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--lux-border-2)' }}>
      {/* Intestazione anteprima */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: 'var(--lux-surface-3)', borderBottom: '1px solid var(--lux-border-1)' }}>
        <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--lux-text-muted-3)' }}>
          Anteprima con dati di esempio
        </p>
        <button onClick={onCopy} data-testid={`copy-preview-${type}`}
          className="flex items-center gap-1.5 text-xs transition-colors hover:text-white"
          style={{ color: 'var(--lux-text-muted-3)' }}>
          <Copy size={11} /> Copia
        </button>
      </div>
      {/* Bolla WhatsApp style */}
      {type === 'whatsapp' ? (
        <div className="p-4" style={{ background: '#0a1a0f' }}>
          <div className="max-w-xs ml-auto rounded-2xl rounded-tr-sm px-4 py-3 text-sm whitespace-pre-wrap" style={{ background: '#25D366', color: '#fff' }}>
            {filled}
          </div>
        </div>
      ) : (
        <div className="p-4 text-sm whitespace-pre-wrap font-mono" style={{ background: 'var(--lux-surface-2)', color: 'var(--lux-text-sec)', lineHeight: '1.6' }}>
          {filled}
        </div>
      )}
    </div>
  );
}
